import { Box, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import { ColumnDef } from '@tanstack/react-table';
import { useDeleteQaChunk, useGetQaChunks, useImportQaChunk } from 'apis/business/agent/qaChunks/hook';
import { QaChunkResponse } from 'apis/business/agent/qaChunks/types';
import DownloadIcon from 'assets/icons/DownloadIcon';
import ExportIcon from 'assets/icons/ExportIcon';
import ImportIcon from 'assets/icons/ImportIcon';
import Button from 'components/Common/Button';
import DataTable from 'components/Common/DataTable';
import Pagination from 'components/Common/Pagination';
import Search from 'components/Common/Search';
import { useState } from 'react';
import { GoPlus } from 'react-icons/go';
import { MdOutlineEdit } from 'react-icons/md';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { Link, useParams } from 'react-router-dom';
import { fileDownloader } from 'utils/helperFunctions';
import http from 'utils/http';

export default function QuestionAnswer() {
  const theme = useTheme();
  const { workspaceId, agentId } = useParams();
  const [searchText, setSearchText] = useState<null | string>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const { mutateAsync: importQaChunk } = useImportQaChunk();
  const { mutateAsync: deleteQaChunk } = useDeleteQaChunk();
  const { data: qaChunks, isLoading: isQaChunkLoading } = useGetQaChunks({
    page: currentPage,
    'filter[content]': searchText
  });

  const handleExport = async () => {
    const response = await http.getFile(`business/workspaces/${workspaceId}/agents/${agentId}/qa_chunks/export`);
    fileDownloader(response, 'agent-qa.csv');
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const formData = new FormData();
      formData.append('file', file);
      await importQaChunk(formData);
    }
  };
  const triggerFileInput = () => {
    const inputElement = document.getElementById('import-qa-chunk') as HTMLInputElement;
    inputElement?.click();
  };

  return (
    <>
      <Box display='flex' justifyContent='space-between' alignItems='center' mb={2}>
        <Typography variant='h2' fontWeight={500}>
          Q&A
        </Typography>
        <Box display='flex' gap={2}>
          <Search
            size='small'
            sx={{ background: theme.palette.background.paper }}
            placeholder='Search Question'
            searchText={searchText}
            setSearchText={setSearchText}
          />
          <Button
            sx={{ background: theme.palette.background.paper, border: '1px solid #ECECEC' }}
            color='secondary'
            variant='outlined'
            startIcon={<ExportIcon />}
            onClick={() => handleExport()}
          >
            Export
          </Button>
          <label htmlFor='import-qa-chunk'>
            <input
              type='file'
              name='import-qa-chunk'
              id='import-qa-chunk'
              style={{ display: 'none' }}
              accept='.csv'
              onChange={handleFileChange}
            />
            <Button
              sx={{ background: theme.palette.background.paper, border: '1px solid #ECECEC' }}
              color='secondary'
              variant='outlined'
              startIcon={<ImportIcon />}
              onClick={triggerFileInput}
            >
              Import
            </Button>
          </label>
          <Tooltip title='Download sample file' arrow placement='top'>
            <IconButton
              href='/sampleQa.csv'
              download={true}
              sx={{ background: theme.palette.background.paper, border: '1px solid #ECECEC' }}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Link to='qa-chunk/add'>
            <Button color='primary' startIcon={<GoPlus />}>
              Add New
            </Button>
          </Link>
        </Box>
      </Box>
      <DataTable
        data={qaChunks?.data}
        columns={columns(deleteQaChunk)}
        isHeaderSticky={true}
        isLoading={isQaChunkLoading}
      />
      <Pagination totalPages={qaChunks?.meta?.total_pages} currentPage={currentPage} onChange={setCurrentPage} />
    </>
  );
}

const columns = (deleteQaChunk: (id: string) => void): ColumnDef<QaChunkResponse>[] => [
  {
    header: 'Question',
    accessorKey: 'question'
  },
  {
    header: 'Answer',
    accessorKey: 'answer'
  },
  {
    header: 'Action',
    cell: (info) => (
      <Box display='flex' gap={1}>
        <Link to={`qa-chunk/${String(info.row.original.chunk_id)}/edit`}>
          <IconButton>
            <MdOutlineEdit />
          </IconButton>
        </Link>
        <IconButton color='error' onClick={() => deleteQaChunk(String(info.row.original.chunk_id))}>
          <RiDeleteBin6Line style={{ color: 'red' }} />
        </IconButton>
      </Box>
    )
  }
];
